/**
 * Pipeline & Facility helpers (fieldiq-api#1196, PF-1): who may press what,
 * how a refusal reads, the status badge, and the unit-labelled number.
 *
 * Nothing here converts a unit or computes a figure the API returns: chainage,
 * OD / WT, MOP and segment length arrive in the tenant's units already.
 */
import type { PfMeta, PfUnits } from '../types/pipeline-facility';
import { fmtQty } from './display-labels';
import { formatCalendarDate } from './calendar-date';

// ─── Who may press what (mirrors fieldiq-api lib/pf-roles.ts) ────────────────
//
// For hiding a button, not for enforcement. The API's requireRole is a plain
// membership test (owner is NOT on any list), so a button these refuse is left
// off; a 403 that still arrives is shown as a refusal, never as success.

export const PF_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const PF_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const PF_SEGMENT_WRITE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** Settings, customer settings, and site create / edit / delete. */
export const canAdminPf = inList(PF_ADMIN_ROLES);
/** Segment add / edit / delete — the office plus the supervisor. */
export const canWriteSegments = inList(PF_SEGMENT_WRITE_ROLES);

// PF-2 (fieldiq-api#1200).
export const PF_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const PF_WORK_ROLES = [...PF_DISPATCH_ROLES, 'field_worker'] as const;
export const PF_RENT_IN_READ_ROLES = [...PF_DISPATCH_ROLES, 'logistics'] as const;
/** Job create / edit / status / delete, crew, equipment, rent-ins, and deleting a log. */
export const canDispatchPf = inList(PF_DISPATCH_ROLES);
/** Writing a daily log. A field hand also needs to be on the job's crew (the API's 403 not_on_crew). */
export const canWorkPf = inList(PF_WORK_ROLES);
/** The rent-in rate, unit, billable and PO — the API drops them for anyone else. */
export const canSeeRentInCost = inList(PF_RENT_IN_READ_ROLES);
/** A field hand sees only the jobs they are crewed on — on the register and in detail. */
export const isPfFieldHand = (role: string | null | undefined) => role === 'field_worker';

// ─── Refusals ────────────────────────────────────────────────────────────────

export interface PfApiError {
  status: number | null;
  code: string | null;
  message: string | null;
  conflictingSegmentId: string | null;
  /** The rest of the refusal body (allowed, today, workDate, checkDay, existingId …). */
  extra: Record<string, unknown>;
}

export function pfApiError(err: unknown): PfApiError {
  const r = (err as {
    response?: { status?: unknown; data?: { error?: unknown; code?: unknown; conflictingSegmentId?: unknown } };
  })?.response;
  const d = r?.data;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code: typeof d?.code === 'string' ? d.code : null,
    message: typeof d?.error === 'string' && d.error ? d.error : null,
    conflictingSegmentId: typeof d?.conflictingSegmentId === 'string' ? d.conflictingSegmentId : null,
    extra: d && typeof d === 'object' ? (d as Record<string, unknown>) : {},
  };
}

/**
 * A write's error as one sentence. A 403 is said as a refusal whatever the
 * body says, so it can never read as a validation slip or a success. The API's
 * own `error` string otherwise, then the caller's fallback.
 */
export function pfErrorMessage(err: unknown, fallback: string): string {
  const e = pfApiError(err);
  if (e.status === 403) return 'You do not have permission to do this. Ask an admin or office staff.';
  return e.message ?? fallback;
}

/** The segment the 409 named, by label or chainage. Null when it is not a segment_overlap. */
export function segmentOverlapMessage(
  err: unknown,
  segments: readonly { id: string; label: string | null; fromChainage: number | null; toChainage: number | null }[],
  chainageUnit: string | null,
): string | null {
  const e = pfApiError(err);
  if (e.status !== 409 || e.code !== 'segment_overlap') return null;
  const other = e.conflictingSegmentId ? segments.find((s) => s.id === e.conflictingSegmentId) : undefined;
  if (!other) return e.message ?? 'This segment overlaps another segment of this site.';
  const range = `${fmtNumber(other.fromChainage)}–${fmtNumber(other.toChainage)}${chainageUnit ? ` ${chainageUnit}` : ''}`;
  return `Overlaps segment ${other.label ? `${other.label} (${range})` : range}. Change the chainage so the two do not share any length.`;
}

// ─── Display ─────────────────────────────────────────────────────────────────

/** A number as given, grouped; absent → `—`. A real 0 is `0`. Never rounds what the API rounded. */
export function fmtNumber(n: number | null | undefined): string {
  return fmtQty(n, 6);
}

/** `114.3 mm`; absent value → `—` (no unit after a dash); absent unit → the number alone. */
export function withUnit(n: number | null | undefined, unit: string | null | undefined): string {
  const v = fmtNumber(n);
  if (v === '—') return v;
  return unit ? `${v} ${unit}` : v;
}

export const pfUnits = (meta: PfMeta | null | undefined): Partial<PfUnits> => meta?.units ?? {};

export interface PfSkin {
  label: string;
  fill: string;
  rail: string;
}

/** Active green, inactive slate. Anything else claims nothing: a slate `—` and a transparent rail. */
export const PF_STATUS_SKIN: Record<string, PfSkin> = {
  active: { label: 'Active', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  inactive: { label: 'Inactive', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const PF_UNKNOWN_SKIN: PfSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
export const pfStatusSkin = (status: string | null | undefined): PfSkin =>
  status && Object.prototype.hasOwnProperty.call(PF_STATUS_SKIN, status) ? PF_STATUS_SKIN[status] : PF_UNKNOWN_SKIN;

/** The label the API's catalog gives a value; the raw value if the catalog lacks it; `—` when absent. */
export function catalogLabel(list: readonly { value: string; label: string }[] | undefined, value: string | null | undefined): string {
  if (!value) return '—';
  return list?.find((e) => e.value === value)?.label ?? value;
}

/** An input's text as a write value: blank → null, otherwise the trimmed text (the API parses numbers). */
export const blankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/** A read value back into an input. */
export const toInput = (v: number | string | null | undefined): string => (v === null || v === undefined ? '' : String(v));

// ─── PF-2: jobs and daily logs ───────────────────────────────────────────────

/** The job status machine (mirrors fieldiq-api lib/pf-catalog PF_JOB_TRANSITIONS) — for which buttons to show only. */
export const PF_JOB_TRANSITIONS: Record<string, readonly string[]> = {
  draft: ['scheduled', 'cancelled'],
  scheduled: ['draft', 'in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};
export const pfNextStatuses = (status: string | null | undefined): readonly string[] =>
  status && Object.prototype.hasOwnProperty.call(PF_JOB_TRANSITIONS, status) ? PF_JOB_TRANSITIONS[status] : [];
/** Only a draft or scheduled job with no logs can be deleted; the API decides the logs half. */
export const pfJobDeletable = (status: string | null | undefined) => status === 'draft' || status === 'scheduled';
/** A log can be written on a scheduled or in-progress job. */
export const pfJobLoggable = (status: string | null | undefined) => status === 'scheduled' || status === 'in_progress';

export const PF_JOB_STATUS_SKIN: Record<string, PfSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  in_progress: { label: 'In progress', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const pfJobStatusSkin = (status: string | null | undefined): PfSkin =>
  status && Object.prototype.hasOwnProperty.call(PF_JOB_STATUS_SKIN, status) ? PF_JOB_STATUS_SKIN[status] : PF_UNKNOWN_SKIN;

export const PF_RENT_STATUS_SKIN: Record<string, PfSkin> = {
  upcoming: { label: 'Upcoming', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  on_rent: { label: 'On rent', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  off_rent: { label: 'Off rent', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const pfRentStatusSkin = (status: string | null | undefined): PfSkin =>
  status && Object.prototype.hasOwnProperty.call(PF_RENT_STATUS_SKIN, status) ? PF_RENT_STATUS_SKIN[status] : PF_UNKNOWN_SKIN;

/** A calendar day as 'Oct 26, 2026'; never through `new Date('YYYY-MM-DD')`. Absent → `—`. */
export const pfDay = (value: string | null | undefined): string => formatCalendarDate(value) ?? '—';

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * Every refusal code the PF-2 routes send, as one plain sentence. A code this
 * map lacks falls back to the API's own `error` string, then to `fallback`.
 */
const PF_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  invalid_transition: (x) => {
    const allowed = Array.isArray(x.allowed) ? (x.allowed as string[]) : [];
    return allowed.length
      ? `This job cannot move to that status. From here it can go to: ${allowed.map((a) => pfJobStatusSkin(a).label).join(', ')}.`
      : 'This job cannot move to that status from where it is.';
  },
  job_closed: () => 'This job is completed or cancelled, so it can no longer be changed.',
  job_not_deletable: () => 'Only a draft or scheduled job can be deleted. Cancel this one instead.',
  job_has_logs: () => 'This job has daily logs, so it cannot be deleted. Cancel it instead.',
  job_not_scheduled: () => 'This job is still a draft. Schedule it before logging work on it.',
  job_number_contended: () => 'Another job took that number at the same moment. Try again.',
  already_on_crew: () => 'That person is already on the crew.',
  already_on_job: () => 'That unit is already on this job.',
  invalid_rent_dates: () => 'The off-rent date cannot be before the on-rent date.',
  rent_in_hours_outside_dates: (x) =>
    `This unit has hours logged on ${str(x.workDate) ? pfDay(str(x.workDate)) : 'a day'} outside the new rent dates. Keep that day inside them.`,
  rent_in_has_hours: () => 'This rented-in unit has hours on a daily log. Set its off-rent date instead of removing it.',
  future_day: (x) => `A daily log cannot be for a future day. Today is ${str(x.today) ? pfDay(str(x.today)) : 'earlier'}.`,
  daily_log_exists: () => 'This job already has a daily log for that day. Open that log instead.',
  user_not_on_crew: () => 'Hours can only be entered for people on the job\'s crew. Add them to the crew first.',
  equipment_not_on_job: () => 'That unit is not on this job. Add it to the job\'s equipment first.',
  rent_in_not_on_job: () => 'That rented-in unit belongs to another job.',
  rent_in_not_on_rent: () => 'That rented-in unit was not on rent that day.',
  meter_owned_only: () => 'An hour-meter reading can only be recorded for an owned unit.',
  duplicate_crew: () => 'The same person is listed twice. Remove one of the rows.',
  duplicate_unit: () => 'The same unit is listed twice. Remove one of the rows.',
  check_unit_mismatch: () => 'That pre-use check is for a different unit.',
  check_not_pre_use: () => 'Only a pre-use check can be linked to a daily log.',
  check_wrong_day: (x) => `That check was filed on ${str(x.checkDay) ? pfDay(str(x.checkDay)) : 'another day'}, not on this log's day.`,
  site_customer_mismatch: () => 'That site belongs to a different customer.',
  segment_site_mismatch: () => 'That segment belongs to a different site.',
  chainage_outside_segment: () => 'The chainage range must sit inside the segment.',
  not_on_crew: () => 'You are not on this job\'s crew, so you cannot open or log work on it.',
  not_found: () => 'That record no longer exists. It may have been deleted.',
};

/** The codes `pfRefusal` has a sentence for — the test checks each one reads as a sentence. */
export const PF_REFUSAL_CODES = Object.keys(PF_REFUSALS);

/**
 * A PF-2 write's (or read's) error as one plain sentence. A coded refusal reads
 * from the map; a 404 with no code (the API's bare `Not found`) reads as gone;
 * a coded 403 (`not_on_crew`) keeps its own sentence, any other 403 is a
 * permission refusal; `invalid_request` and unknown codes use the API's message.
 */
export function pfRefusal(err: unknown, fallback: string): string {
  const e = pfApiError(err);
  if (e.code && PF_REFUSALS[e.code]) return PF_REFUSALS[e.code](e.extra);
  if (e.status === 404) return PF_REFUSALS.not_found({});
  if (e.status === 403) return 'You do not have permission to do this. Ask an admin or office staff.';
  return e.message ?? fallback;
}

/** A save warning as one sentence (office camelCase or field snake_case). */
export function pfWarningText(
  w: { code: string; entered?: number; previous?: number; previousDate?: string; previous_date?: string },
  unitName?: string | null,
): string {
  if (w.code === 'meter_below_previous') {
    const day = w.previousDate ?? w.previous_date;
    return `${unitName ?? 'A unit'}: hour meter ${fmtNumber(w.entered)} is below the last reading of ${fmtNumber(w.previous)}${
      day ? ` on ${pfDay(day)}` : ''}. Check the meter; the log was saved.`;
  }
  return `Saved with a warning (${w.code}).`;
}
